import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Chip,
    Checkbox,
    Collapse,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress
} from '@mui/material';
import { IconFilter, IconFilterOff, IconPlus, IconAlertTriangle } from '@tabler/icons';
import api from '../../services/api';
import ContratoFormDrawer from './ContratoFormDrawer';
import ContratoDetailDrawer from './ContratoDetailDrawer';
import {
    CONTRATO_STATUS_COLORS,
    CONTRATO_STATUS_LABELS,
    STATUS_PAGAMENTO_COLORS,
    STATUS_PAGAMENTO_LABELS,
    formatServicoDate
} from './servicoUtils';

const FILTROS_INICIAIS = { status: '', status_pagamento: '', servico: '', overdue: false };

// Contratos de Serviço (adesões de cliente) - o catálogo de Produtos/
// Serviços em si mora no hub unificado (`ItemsHubPage.js`), sem relação
// direta com essa tela.
const ServicosPage = () => {
    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Só pro seletor "Serviço" do painel de filtros.
    const [servicosParaFiltro, setServicosParaFiltro] = useState([]);

    const [contratos, setContratos] = useState([]);
    const [loadingContratos, setLoadingContratos] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
    const [selectedContratoId, setSelectedContratoId] = useState(null);
    const [openCreateContrato, setOpenCreateContrato] = useState(false);

    useEffect(() => {
        api
            .get('v1/apps/default/')
            .then((response) => setAppId(response.data.app.id))
            .catch((error) => console.error(error));
    }, []);

    const loadServicosParaFiltro = async () => {
        if (!appId) return;
        try {
            const response = await api.get(`v1/apps/${appId}/catalog_items/?tipo=servico&limit=200`);
            setServicosParaFiltro(response.data?.results || []);
        } catch (error) {
            console.error('Erro ao carregar serviços', error);
        }
    };

    const loadContratos = async () => {
        if (!appId) return;
        setLoadingContratos(true);
        try {
            const query = new URLSearchParams();
            if (filtros.status) query.set('status', filtros.status);
            if (filtros.status_pagamento) query.set('status_pagamento', filtros.status_pagamento);
            if (filtros.servico) query.set('servico', filtros.servico);
            if (filtros.overdue) query.set('overdue', 'true');
            const response = await api.get(`v1/apps/${appId}/contratos_servico/?${query.toString()}`);
            setContratos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar contratos', error);
            setStatus({ type: 'error', message: 'Erro ao carregar Contratos.' });
        } finally {
            setLoadingContratos(false);
        }
    };

    useEffect(() => {
        loadServicosParaFiltro();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    useEffect(() => {
        loadContratos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, filtros]);

    const limparFiltros = () => setFiltros(FILTROS_INICIAIS);
    const filtrosAtivosCount = Object.values(filtros).filter((v) => v !== '' && v !== false).length;

    const displayName = (u) => (u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email : null);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h2">Contratos de Serviço</Typography>
                <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={() => setOpenCreateContrato(true)} sx={{ textTransform: 'none' }}>
                    Novo Contrato
                </Button>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Button
                    variant="outlined"
                    startIcon={<IconFilter size={18} />}
                    onClick={() => setShowFilters((prev) => !prev)}
                    sx={{ borderRadius: '14px', textTransform: 'none' }}
                >
                    <Badge badgeContent={filtrosAtivosCount} color="primary" sx={{ '& .MuiBadge-badge': { right: -10 } }}>
                        Filtros
                    </Badge>
                </Button>
                {filtrosAtivosCount > 0 && (
                    <Button variant="text" startIcon={<IconFilterOff size={18} />} onClick={limparFiltros} sx={{ textTransform: 'none' }}>
                        Limpar filtros
                    </Button>
                )}
            </Box>

            <Collapse in={showFilters}>
                <Paper sx={{ p: 2.5, borderRadius: '20px', mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Status"
                                value={filtros.status}
                                onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {Object.entries(CONTRATO_STATUS_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Pagamento"
                                value={filtros.status_pagamento}
                                onChange={(e) => setFiltros({ ...filtros, status_pagamento: e.target.value })}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {Object.entries(STATUS_PAGAMENTO_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Serviço"
                                value={filtros.servico}
                                onChange={(e) => setFiltros({ ...filtros, servico: e.target.value })}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {servicosParaFiltro.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.nome}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={filtros.overdue} onChange={(e) => setFiltros({ ...filtros, overdue: e.target.checked })} />
                                }
                                label="Vencidos"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            {loadingContratos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : contratos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h5" sx={{ opacity: 0.4, fontWeight: 700 }}>
                        Nenhum Contrato encontrado.
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: '16px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Serviço</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Pagamento</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contratos.map((c) => (
                                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedContratoId(c.id)}>
                                    <TableCell>{displayName(c.cliente_detail) || '—'}</TableCell>
                                    <TableCell>{c.servico_detail?.nome}</TableCell>
                                    <TableCell>R$ {c.valor}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={CONTRATO_STATUS_LABELS[c.status] || c.status}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor: CONTRATO_STATUS_COLORS[c.status]?.bg,
                                                color: CONTRATO_STATUS_COLORS[c.status]?.color
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {formatServicoDate(c.data_vencimento)}
                                            {c.is_overdue && <IconAlertTriangle size={15} color="#B42121" />}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={STATUS_PAGAMENTO_LABELS[c.status_pagamento] || c.status_pagamento}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor: STATUS_PAGAMENTO_COLORS[c.status_pagamento]?.bg,
                                                color: STATUS_PAGAMENTO_COLORS[c.status_pagamento]?.color
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {openCreateContrato && (
                <ContratoFormDrawer
                    appId={appId}
                    onClose={() => setOpenCreateContrato(false)}
                    onSaved={() => {
                        setOpenCreateContrato(false);
                        loadContratos();
                    }}
                />
            )}

            {selectedContratoId && (
                <ContratoDetailDrawer
                    appId={appId}
                    contratoId={selectedContratoId}
                    onClose={() => setSelectedContratoId(null)}
                    onChanged={loadContratos}
                />
            )}
        </Box>
    );
};

export default ServicosPage;
